import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createAgent, getAgent, updateAgent } from "@/services/agent-service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CheckIcon from "@/assets/icons/icon-check.svg";
import { CATEGORIES } from "@/constants/categories";
import IconFile from "@/assets/icons/ic-file.svg";
import IconArrowLeft from "@/assets/icons/ic-arrow-left1.svg";
import IconChevronDown from "@/assets/icons/ic-chevron-down.svg";
import { MainCategory } from "@/types/agent";
import { FormField } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAIN_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "horizontal", label: "Horizontal (General/Business Support)" },
  { value: "vertical", label: "Vertical (Industry-Specific)" },
  { value: "enabler", label: "Enabler (Agent Building Support)" },
];

interface NewAgentPageProps {
  agentId?: string;
}

export default function NewAgentPage({ agentId }: NewAgentPageProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [solution, setSolution] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [applicationArea, setApplicationArea] = useState("");
  const [mainCategory, setMainCategory] = useState<MainCategory>(MainCategory.HORIZONTAL);
  const [isClassificationOpen, setIsClassificationOpen] = useState(false);
  const classificationRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = useMemo(
    () =>
      name.trim() !== "" &&
      provider.trim() !== "" &&
      introduction.trim() !== "" &&
      solution.trim() !== "" &&
      selectedCategories.length > 0 &&
      applicationArea.trim() !== "" &&
      mainCategory.trim() !== "",
    [name, provider, introduction, solution, selectedCategories, applicationArea, mainCategory],
  );

  useEffect(() => {
    if (agentId) {
      const fetchAgentData = async () => {
        try {
          const data = await getAgent(agentId);
          setName(data.name || "");
          setProvider(data.provider || "");
          setIntroduction(data.short_description || "");
          setSolution(data.solution || "");
          setSelectedCategories(data.categories || []);
          setApplicationArea(data.application_area || "");
          setMainCategory(data.main_category || MAIN_CATEGORY_OPTIONS[0].value);
        } catch (error) {
          toast({
            variant: "destructive",
            description: "에이전트 정보를 불러오는 데 실패했습니다.",
          });
        }
      };
      fetchAgentData();
    }
  }, [agentId, toast]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (classificationRef.current && !classificationRef.current.contains(event.target as Node)) {
        setIsClassificationOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    );
  };

  const handleSave = async (isDraft: boolean) => {
    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        provider: provider.trim(),
        short_description: introduction.trim(),
        solution: solution.trim(),
        categories: selectedCategories,
        application_area: applicationArea.trim(),
        main_category: mainCategory,
      };

      if (agentId) {
        await updateAgent(agentId, payload, isDraft);
        if (!isDraft) toast({ description: "에이전트 수정이 완료되었습니다." });
      } else {
        await createAgent(payload, isDraft);
        if (!isDraft) toast({ description: "에이전트 생성이 완료되었습니다." });
      }
      navigate("/my-agents");
    } catch {
      toast({
        variant: "destructive",
        description: "오류가 발생했습니다. 다시 시도해 주세요.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 px-5 py-6 lg:px-[160px] lg:py-10 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="w-12 h-12 rounded-full bg-black/[0.04] hover:bg-black/[0.08]"
        >
          <img src={IconArrowLeft} alt="Icon Arrow Left" />
        </Button>
        <h1 className="flex-1 text-xl font-bold leading-7 tracking-[-0.02em] text-[#202020]">
          {agentId ? "에이전트 수정" : "에이전트 등록"}
        </h1>
        <div className="flex gap-2 justify-end">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex-1 lg:flex-none lg:w-[120px] h-10 lg:h-12 rounded-full bg-black/[0.06] text-sm lg:text-base font-bold text-[#202020] backdrop-blur-[40px] hover:bg-black/[0.06] hover:text-[#202020]"
          >
            취소
          </Button>
          <Button
            variant="ghost"
            disabled={isSaving}
            onClick={() => handleSave(true)}
            className="h-10 lg:h-12 rounded-full bg-[#ff8013]/[0.11] px-4 lg:px-6 text-sm lg:text-base font-bold text-[#BB3902] backdrop-blur-[40px] hover:bg-[#ff8013]/[0.11] hover:text-[#BB3902]"
          >
            <img src={IconFile} alt="Icon File" />
            임시 저장
          </Button>
          <Button
            disabled={!isFormValid || isSaving}
            onClick={() => handleSave(false)}
            className="flex-1 lg:flex-none lg:w-[120px] h-10 lg:h-12 rounded-full bg-[#FD5108] text-sm lg:text-base font-bold text-white hover:bg-[#FD5108]"
          >
            저장
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col gap-6 w-full">
        {/* 에이전트 이름 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">에이전트 이름</span>
              <span className="text-sm text-[#CB3A31]">*</span>
            </div>
          </div>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 50))}
            onBlur={() => setName((v) => v.trim())}
            placeholder="에이전트 이름 입력..."
            className="w-full h-12 px-4 py-3 rounded-lg border-none bg-black/[0.045] text-base font-bold text-[#202020] placeholder:text-[#8D8D8D] placeholder:font-normal tracking-[-0.01em]"
          />
        </div>

        {/* 공급사 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">공급사</span>
              <span className="text-sm text-[#CB3A31]">*</span>
            </div>
          </div>
          <Input
            type="text"
            value={provider}
            onChange={(e) => setProvider(e.target.value.slice(0, 50))}
            onBlur={() => setProvider((v) => v.trim())}
            placeholder="공급사 입력..."
            className="w-full h-12 px-4 py-3 rounded-lg border-none bg-black/[0.045] text-base font-bold text-[#202020] placeholder:text-[#8D8D8D] placeholder:font-normal tracking-[-0.01em]"
          />
        </div>

        {/* 한 줄 소개 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">한 줄 소개</span>
              <span className="text-sm text-[#CB3A31]">*</span>
              <span className="text-sm text-[#8D8D8D]">(50자 미만)</span>
            </div>
            <span className="text-sm font-bold text-[#BBBBBB]">{introduction.length}/50자</span>
          </div>
          <Textarea
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value.slice(0, 50))}
            onBlur={() => setIntroduction((v) => v.trim())}
            placeholder="간단한 소개..."
            rows={3}
            className="w-full px-4 py-3 rounded-lg border-none bg-black/[0.045] text-base font-bold text-[#202020] placeholder:text-[#8D8D8D] placeholder:font-normal tracking-[-0.01em] resize-y"
          />
        </div>

        {/* 솔루션 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">솔루션</span>
              <span className="text-sm text-[#CB3A31]">*</span>
            </div>
          </div>
          <Textarea
            value={solution}
            onChange={(e) => setSolution(e.target.value.slice(0, 500))}
            placeholder="이 에이전트가 제공하는 솔루션에 대해 설명하세요..."
            rows={4}
            className="w-full px-4 py-3 rounded-lg border-none bg-black/[0.045] text-base font-bold text-[#202020] placeholder:text-[#8D8D8D] placeholder:font-normal tracking-[-0.01em] resize-y"
          />
        </div>

        {/* 대분류 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <span className="text-sm text-[#202020]">대분류</span>
            <span className="text-sm text-[#CB3A31]">*</span>
          </div>
          <div className="relative">
            {/* Major Category */}
            <Select
              value={mainCategory}
              onValueChange={(val) => {
                setMainCategory(val as MainCategory);
              }}
            >
              <SelectTrigger className="w-full h-12 px-4 rounded-lg border-none bg-[rgba(0,0,0,0.05)] text-base text-[#202020] focus:ring-0 focus:ring-offset-0">
                <SelectValue placeholder="대분류 선택..." />
              </SelectTrigger>
              <SelectContent className="font-bold">
                <SelectItem value={MainCategory.HORIZONTAL}>
                  Horizontal (General/Business Support)
                </SelectItem>
                <SelectItem value={MainCategory.VERTICAL}>Vertical (Industry-Specific)</SelectItem>
                <SelectItem value={MainCategory.ENABLER}>
                  Enabler (Agent Building Support)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 상세 기능 카테고리 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">상세 기능 카테고리</span>
              <span className="text-sm text-[#CB3A31]">*</span>
              <span className="text-sm text-[#8D8D8D]">(다중 선택)</span>
            </div>
            {selectedCategories.length > 0 && (
              <span className="text-sm font-bold text-[#202020]">
                {selectedCategories.length}개 카테고리 선택됨
              </span>
            )}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => toggleCategory(category)}
                className={`flex items-center gap-2 px-4 py-3 border rounded-xl bg-white text-left transition-colors  "border-black/[0.09] hover:border-black/[0.15]"
                }`}
              >
                {selectedCategories.includes(category) ? (
                  <img src={CheckIcon} alt="" className="w-5 h-5 flex-shrink-0 rounded" />
                ) : (
                  <div className="w-5 h-5 rounded border bg-white border-black/[0.15] flex-shrink-0" />
                )}
                <span className="text-base text-[#202020] tracking-[-0.01em]">{category}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 적용 분야 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className="text-sm text-[#202020]">적용 분야</span>
              <span className="text-sm text-[#CB3A31]">*</span>
            </div>
          </div>
          <Input
            type="text"
            value={applicationArea}
            onChange={(e) => setApplicationArea(e.target.value.slice(0, 500))}
            onBlur={() => setApplicationArea((v) => v.trim())}
            placeholder="적용 분야 입력..."
            className="w-full h-12 px-4 py-3 rounded-lg border-none bg-black/[0.045] text-base font-bold text-[#202020] placeholder:text-[#8D8D8D] placeholder:font-normal tracking-[-0.01em]"
          />
        </div>
      </div>
    </div>
  );
}
